import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Globe,
  Briefcase,
  AlertTriangle,
  CloudUpload,
  Mail,
  Building2,
  GraduationCap,
  Shield,
  BarChart3,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RTooltip,
  Legend,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { getBrowserHistory, getBrowserCategories, getBrowserAnalytics } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import dayjs from 'dayjs';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { StatTile } from '../components/ui/stat-tile';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

function BrowserHistoryPage() {
  const { workerId } = useParams();
  const [history, setHistory] = useState([]);
  const [categories, setCategories] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterParams, setFilterParams] = useState({ startDate: null, endDate: null, hours: null });
  const itemsPerPage = 500;

  useEffect(() => {
    loadData();
  }, [workerId, page, filterParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [histRes, catRes, analyticsRes] = await Promise.all([
        getBrowserHistory(
          null,
          itemsPerPage,
          workerId ? parseInt(workerId) : null,
          filterParams.startDate,
          filterParams.endDate
        ),
        getBrowserCategories(
          workerId ? parseInt(workerId) : null,
          filterParams.startDate,
          filterParams.endDate
        ),
        getBrowserAnalytics(
          workerId ? parseInt(workerId) : null,
          filterParams.startDate,
          filterParams.endDate
        ),
      ]);
      setHistory(histRes.data.data);
      setTotalCount(histRes.data.count || histRes.data.data.length);
      setCategories(catRes.data.data || {});
      setAnalytics(analyticsRes.data.data);
    } catch (err) {
      console.error('Failed to load browser data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (params) => {
    setFilterParams(params);
    setPage(1);
  };

  const getCategoryIcon = (category) => {
    const cls = 'h-5 w-5';
    switch (category) {
      case 'work':
        return <Briefcase className={`${cls} text-success`} />;
      case 'job_search':
        return <Building2 className={`${cls} text-destructive`} />;
      case 'personal_email':
        return <Mail className={`${cls} text-warning`} />;
      case 'cloud_storage':
        return <CloudUpload className={`${cls} text-warning`} />;
      case 'competitor':
        return <AlertTriangle className={`${cls} text-destructive`} />;
      case 'social_media':
        return <GraduationCap className={`${cls} text-primary`} />;
      default:
        return <Globe className={`${cls} text-muted-foreground`} />;
    }
  };

  const getRiskVariant = (score) => {
    if (score >= 40) return 'destructive';
    if (score >= 20) return 'warning';
    if (score > 0) return 'secondary';
    return 'success';
  };

  const getRiskTone = (score) => {
    if (score >= 40) return 'destructive';
    if (score >= 20) return 'warning';
    if (score > 0) return 'primary';
    return 'success';
  };

  const categoryData = Object.entries(categories).map(([name, data]) => ({
    name: name.replace('_', ' ').toUpperCase(),
    value: data.count,
  }));

  const browserData = analytics?.browser_distribution
    ? Object.entries(analytics.browser_distribution).map(([name, value]) => ({
        name: name || 'Unknown',
        value: value,
      }))
    : [];

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="Browser Activity"
        description="Monitor web browsing history and detect suspicious access"
      />

      <DateRangeFilter onFilterChange={handleFilterChange} />

      {analytics && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label="Total Visits"
            value={analytics.total_visits.toLocaleString()}
            icon={Globe}
            tone="primary"
          />
          <StatTile
            label="Unique Domains"
            value={analytics.total_unique_domains.toLocaleString()}
            icon={Globe}
            tone="success"
          />
          <StatTile
            label="Unique URLs"
            value={analytics.total_unique_urls.toLocaleString()}
            icon={BarChart3}
            tone="warning"
          />
          <StatTile
            label="Avg Risk Score"
            value={analytics.average_risk_score.toFixed(1)}
            icon={Shield}
            tone={getRiskTone(analytics.average_risk_score)}
          />
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {analytics && browserData.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Browser Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={browserData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {browserData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {analytics && analytics.hourly_activity && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Hourly Activity Pattern</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsBarChart data={analytics.hourly_activity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <RTooltip />
                  <Bar dataKey="visits" fill="#8884d8" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Browsing Categories</CardTitle>
          </CardHeader>
          <CardContent>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Alert variant="info">
                <AlertDescription>No browser data available yet</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Risk by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {Object.entries(categories).map(([cat, data]) => (
                <li key={cat} className="flex items-center gap-3 py-3">
                  {getCategoryIcon(cat)}
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground">
                      {cat.replace('_', ' ').toUpperCase()}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {data.count} visits • Risk: {data.total_risk}
                    </div>
                  </div>
                  <Badge variant={getRiskVariant(data.total_risk / Math.max(data.count, 1))}>
                    {data.total_risk}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {analytics && analytics.top_domains && analytics.top_domains.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">Top Domains</CardTitle>
              <Badge variant="outline">{analytics.total_unique_domains} Unique</Badge>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domain</TableHead>
                    <TableHead className="text-right">Visits</TableHead>
                    <TableHead className="text-right">Unique URLs</TableHead>
                    <TableHead className="text-right">Risk</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analytics.top_domains.slice(0, 10).map((domain, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="max-w-[200px] truncate font-medium text-foreground">
                        {domain.domain}
                      </TableCell>
                      <TableCell className="text-right text-foreground">
                        {domain.visits.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline">{domain.unique_urls || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={getRiskVariant(domain.risk_score)}>
                          {domain.risk_score.toFixed(1)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {analytics && analytics.top_urls && analytics.top_urls.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Most Visited URLs</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="max-h-[400px] divide-y divide-border overflow-auto">
                {analytics.top_urls.slice(0, 15).map((urlItem, idx) => (
                  <li key={idx} className="flex items-center gap-3 py-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="max-w-[400px] truncate text-sm text-foreground">
                        {urlItem.url}
                      </div>
                      <div className="text-xs text-muted-foreground">{urlItem.visits} visits</div>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Recent Browsing History ({totalCount.toLocaleString()} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <Alert variant="info">
              <AlertDescription>
                No browser history recorded yet. History will appear as you browse.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <ul className="max-h-[500px] divide-y divide-border overflow-auto">
                {history.map((item) => (
                  <li key={item.id} className="flex items-start gap-3 py-3">
                    {getCategoryIcon(item.category)}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="max-w-[500px] flex-1 truncate text-sm text-foreground">
                          {item.title || item.url}
                        </span>
                        <Badge variant="outline">{item.browser}</Badge>
                        <Badge variant="secondary">{item.category || 'browsing'}</Badge>
                        {item.risk_score > 0 && (
                          <Badge variant={getRiskVariant(item.risk_score)}>
                            Risk: {item.risk_score}
                          </Badge>
                        )}
                        {item.visit_count > 1 && <Badge variant="outline">{item.visit_count}x</Badge>}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {dayjs(item.timestamp).format('MMM DD, YYYY HH:mm:ss')} • {item.domain || item.url}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default BrowserHistoryPage;
